class ProductCardManager {
  constructor() {
    this.initializeCards();
    this.setupEventListeners();
  }

  initializeCards() {
    // The constructor can now store the cards it manages.
    this.cards = document.querySelectorAll(".product-card");

    if (this.cards.length === 0) {
      console.warn('⚠️ No product-cards found. Retrying in 100ms...');

      // Retry after a small delay
      setTimeout(() => {
        this.cards = document.querySelectorAll(".product-card");
        if (this.cards.length > 0) {
          this.init();
        }
      }, 100);
    } else {
      this.init();
    }
  }

  setupEventListeners() {
    // Listen for Shopify section load events
    document.addEventListener('shopify:section:load', () => {
      this.initializeCards();
    });

    document.addEventListener('shopify:section:reorder', () => {
      this.initializeCards();
    });

    // Also listen for DOM changes (for dynamic content)
    const observer = new MutationObserver((mutations) => {
      let hasNewProductCards = false;
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            const element = node;
            if (element.classList?.contains('product-card') ||
              element.querySelector?.('.product-card')) {
              hasNewProductCards = true;
            }
          }
        });
      });

      if (hasNewProductCards) {
        this.initializeCards();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  init() {
    document.body.addEventListener("click", (event) => {
      const quickBuyButton = event?.target?.closest(".quick-buy__button");
      const closeButton = event?.target?.closest(".quick-view__close");

      if (quickBuyButton) {
        this.handleQuickBuyOpen(quickBuyButton);
      } else if (closeButton) {
        this.handleQuickViewClose(closeButton);
      } else {
        this.closeAllActivePanels(event?.target);
      }
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        const activeCard = document.querySelector(
          ".product-card.is-quick-view-visible"
        );
        if (activeCard) {
          this.closePanel(activeCard);
        }
      }
    });

    // Iterate over each card to initialize its internal form.
    this.cards.forEach((card) => {
      this.initializeVariantSelector(card);
    });
  }

  /**
   * Handles form submission for filters.
   * @param {any} card - The form submission event object.
   */
  initializeVariantSelector(card) {
    const form = card.querySelector(".quick-buy__form");
    // Ensure that the variant JSON and form exist.
    const variantDataNode = card.querySelector("[data-variant-json]");

    const validHtml = variantDataNode instanceof HTMLElement;
    if (!form || !variantDataNode || !validHtml) {
      console.error('❌ Elementos requeridos no encontrados');
      return;
    }

    const variantData = JSON.parse(variantDataNode.textContent);
    const submitButton = form.querySelector('button[type="submit"]');
    const hiddenInput = form.querySelector('input[name="id"]');
    const swatchGroups = form.querySelectorAll(".js-product-form__input");

    if (!submitButton || !hiddenInput || !swatchGroups.length) {
      console.error('❌ Elementos del formulario no encontrados');
      return;
    }
    // Internal function to update the selected variant in THIS card.
    const updateVariant = () => {
      const selectedOptions = Array.from(swatchGroups).map((group) => {
        const checkedInput = group.querySelector("input:checked");
        // If none is selected, select the first available
        if (!checkedInput) {
          const firstAvailableInput = group.querySelector("input:not(:disabled)");
          if (firstAvailableInput) {
            firstAvailableInput.checked = true;
            return firstAvailableInput.value;
          }
        }
        return checkedInput?.value;
      });

      // Filter undefined options
      const validSelectedOptions = selectedOptions.filter(option => option !== undefined);

      const matchedVariant = variantData.find((variant) => {
        return validSelectedOptions.every((option, index) => {
          return variant.options[index] === option;
        });
      });

      if (matchedVariant) {
        hiddenInput.value = matchedVariant.id;

        if (matchedVariant.available) {
          submitButton.disabled = false;
          submitButton.textContent = "Quick Buy";
        } else {
          submitButton.disabled = true;
          submitButton.textContent = "Sold Out";
        }
      } else {
        submitButton.disabled = true;
        submitButton.textContent = "Unavailable";
      }

      // Update swatch availability AFTER updating the variant
      updateSwatchAvailability(selectedOptions);
    };

    // Internal function to update the visual availability of swatches.
    /**
     * Updates the visual availability of options based on current selections
     * @param {string[]} currentSelections - Array with currently selected options
     */
    const updateSwatchAvailability = (currentSelections) => {
      // For each option group (color, size, etc.)
      swatchGroups.forEach((group, groupIndex) => {
        const swatches = group.querySelectorAll(".variant-swatch");

        swatches.forEach((swatch) => {
          const input = swatch.querySelector("input");
          const value = input.value;

          // Create a test array with this option
          let testSelections = [...currentSelections];
          testSelections[groupIndex] = value;

          // Check if any available variant exists with this combination
          // We only verify the already selected options
          const isAvailable = variantData.some((variant) => {
            if (!variant.available) return false;

            // Verify that all selected options match
            return testSelections.every((option, index) => {
              // If there's no selection at this index (undefined), ignore it
              if (option === undefined) return true;
              return variant.options[index] === option;
            });
          });

          // Update classes and input state
          swatch.classList.toggle("is-unavailable", !isAvailable);
          input.disabled = !isAvailable;

          // For labels, update aria-disabled
          const label = swatch.querySelector(".swatch-label, .swatch-label--text");
          if (label) {
            label.setAttribute("aria-disabled", !isAvailable);
          }
        });
      });
    };

    // Función para inicializar las selecciones por defecto
    const initializeDefaultSelections = () => {

      // Primero verificar si ya hay opciones seleccionadas en el HTML
      const alreadySelectedInputs = Array.from(swatchGroups).map(group => {
        return group.querySelector('input:checked');
      });

      const hasPreselectedOptions = alreadySelectedInputs.some(input => input !== null);

      if (hasPreselectedOptions) {
        return;
      }

      // Si no hay preselecciones, buscar la primera variante disponible
      const firstAvailableVariant = variantData.find(variant => variant.available);

      if (firstAvailableVariant) {
        // Seleccionar las opciones de la primera variante disponible
        swatchGroups.forEach((group, index) => {
          const targetValue = firstAvailableVariant.options[index];
          const targetInput = group.querySelector(`input[value="${targetValue}"]`);
          if (targetInput) {
            // Deseleccionar todos los inputs del grupo
            group.querySelectorAll('input').forEach(input => input.checked = false);
            // Seleccionar el target
            targetInput.checked = true;
          }
        });
      } else {
        // If there are no available variants, select the first option of each group
        swatchGroups.forEach((group) => {
          const firstInput = group.querySelector("input");
          if (firstInput && !group.querySelector("input:checked")) {
            firstInput.checked = true;
          }
        });
      }

      // After selecting initial options, update availability
      const initialSelections = Array.from(swatchGroups).map((group) => {
        const checkedInput = group.querySelector("input:checked");
        return checkedInput?.value;
      });

      // Update the availability of all options based on the initial selection
      updateSwatchAvailability(initialSelections);
    };

    // Attach the change listener to THIS CARD's form.
    form.addEventListener("change", (event) => {
      updateVariant();
    });

    // Initialize default selections and then update
    initializeDefaultSelections();
    updateVariant();
  }

  /**
   * Handles opening the Quick Buy panel.
   * @param {Element} button - The button element that was clicked.
   */
  handleQuickBuyOpen(button) {
    const card = button.closest(".product-card");
    if (card) {
      const currentlyOpen = document.querySelector(
        ".product-card.is-quick-view-visible"
      );
      if (currentlyOpen && currentlyOpen !== card) {
        this.closePanel(currentlyOpen);
      }
      this.openPanel(card);
    }
  }

  /**
   * Handles closing the Quick View panel.
   * @param {Element} button - The close button element that was clicked.
   */
  handleQuickViewClose(button) {
    const card = button.closest(".product-card");
    if (card) {
      this.closePanel(card);
    }
  }

  /**
   * Opens the Quick View panel for a product card.
   * @param {Element} cardElement - The product card element to open the panel for.
   */
  openPanel(cardElement) {
    cardElement.classList.add("is-quick-view-visible");
  }

  /**
   * Closes the Quick View panel for a product card.
   * @param {Element} cardElement - The product card element to close the panel for.
   */
  closePanel(cardElement) {
    cardElement.classList.remove("is-quick-view-visible");
  }

  /**
   * Closes all active Quick View panels when clicking outside.
   * @param {Element} clickedElement - The element that was clicked.
   */
  closeAllActivePanels(clickedElement) {
    const activeCard = document.querySelector(
      ".product-card.is-quick-view-visible"
    );

    // Detailed log for debugging
    if (activeCard) {
      const clickedProductCard = clickedElement?.closest('.product-card');
      const activeProductId = activeCard.getAttribute('data-product-id');
      const clickedProductId = clickedProductCard?.getAttribute('data-product-id');
    }

    // FIX: Check if the click is on a card of the SAME product
    // Instead of only verifying if it's inside the same DOM element
    const clickedProductCard = clickedElement?.closest('.product-card');
    const activeProductId = activeCard?.getAttribute('data-product-id');
    const clickedProductId = clickedProductCard?.getAttribute('data-product-id');

    const isClickOnSameProduct = activeProductId && clickedProductId && activeProductId === clickedProductId;
    const isClickInsideActiveCard = activeCard?.contains(clickedElement);

    if (activeCard && !isClickInsideActiveCard && !isClickOnSameProduct) {
      this.closePanel(activeCard);
    } else if (activeCard) {
    }
  }
}

// Initialize the manager when the DOM is ready.
document.addEventListener("DOMContentLoaded", () => {
  new ProductCardManager();
});
